import type { ApiErrorPayload } from "@normax-performance/shared-types";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "http://localhost:3000/api/v1";
const CSRF_COOKIE_NAME = "normax_performance_csrf";
const CSRF_HEADER_NAME = "X-CSRF-Token";

export class ApiError extends Error {
  constructor(
    public readonly code: ApiErrorPayload["code"],
    message: string,
    public readonly status: number,
    public readonly details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]!) : null;
}

const MUTATING_METHODS = new Set(["POST", "PATCH", "PUT", "DELETE"]);

export async function apiRequest<T>(
  path: string,
  options: { method?: string; body?: unknown; query?: Record<string, string | undefined> } = {},
): Promise<T> {
  const method = options.method ?? "GET";
  const url = new URL(`${API_BASE_URL}${path}`);
  if (options.query) {
    for (const [key, value] of Object.entries(options.query)) {
      if (value !== undefined) url.searchParams.set(key, value);
    }
  }

  const headers: Record<string, string> = {};
  if (options.body !== undefined) headers["Content-Type"] = "application/json";
  if (MUTATING_METHODS.has(method)) {
    const csrfToken = readCookie(CSRF_COOKIE_NAME);
    if (csrfToken) headers[CSRF_HEADER_NAME] = csrfToken;
  }

  const response = await fetch(url.toString(), {
    method,
    credentials: "include",
    headers,
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
  });

  if (response.status === 204) {
    return undefined as T;
  }

  const payload = (await response.json().catch(() => null)) as
    | { data: T }
    | { error: ApiErrorPayload }
    | null;

  if (!response.ok || !payload || "error" in payload) {
    const error = payload && "error" in payload ? payload.error : null;
    throw new ApiError(
      error?.code ?? "INTERNAL",
      error?.message ?? "Došlo je do neočekivane greške.",
      response.status,
      error?.details,
    );
  }

  return payload.data;
}

const CONTENT_DISPOSITION_FILENAME = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i;

function filenameFromContentDisposition(header: string | null): string | null {
  if (!header) return null;
  const match = CONTENT_DISPOSITION_FILENAME.exec(header);
  if (!match) return null;
  if (match[1]) {
    try {
      return decodeURIComponent(match[1]);
    } catch {
      return match[2] ?? null;
    }
  }
  return match[2] ?? null;
}

/**
 * A POST that returns a binary file (e.g. `POST /reports/pdf` →
 * `application/pdf`) rather than the `{data}` JSON envelope. Sends the same
 * credentials + CSRF header as `apiRequest`, and still surfaces a JSON error
 * envelope as an `ApiError` (the backend throws BEFORE writing any bytes, so
 * an error response is always JSON). Returns the blob plus the server's
 * suggested filename.
 */
export async function apiRequestFile(
  path: string,
  body: unknown,
): Promise<{ blob: Blob; filename: string | null }> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  const csrfToken = readCookie(CSRF_COOKIE_NAME);
  if (csrfToken) headers[CSRF_HEADER_NAME] = csrfToken;

  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: "POST",
    credentials: "include",
    headers,
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const errorPayload = (await response.json().catch(() => null)) as { error?: ApiErrorPayload } | null;
    const error = errorPayload?.error ?? null;
    throw new ApiError(
      error?.code ?? "INTERNAL",
      error?.message ?? "Došlo je do neočekivane greške.",
      response.status,
      error?.details,
    );
  }

  return {
    blob: await response.blob(),
    filename: filenameFromContentDisposition(response.headers.get("Content-Disposition")),
  };
}

/**
 * A GET that returns a binary file (e.g. `GET /products/import-template` →
 * an .xlsx template) rather than the `{data}` JSON envelope. Same
 * blob/filename extraction as `apiRequestFile`, but no request body/
 * Content-Type and no CSRF header (GET is not in `MUTATING_METHODS`).
 */
export async function apiRequestFileGet(path: string): Promise<{ blob: Blob; filename: string | null }> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: "GET",
    credentials: "include",
  });

  if (!response.ok) {
    const errorPayload = (await response.json().catch(() => null)) as { error?: ApiErrorPayload } | null;
    const error = errorPayload?.error ?? null;
    throw new ApiError(
      error?.code ?? "INTERNAL",
      error?.message ?? "Došlo je do neočekivane greške.",
      response.status,
      error?.details,
    );
  }

  return {
    blob: await response.blob(),
    filename: filenameFromContentDisposition(response.headers.get("Content-Disposition")),
  };
}

/**
 * A multipart POST for file uploads (e.g. `POST /products/import`) that
 * returns the normal `{data}` JSON envelope. Sends the same credentials +
 * CSRF header as `apiRequest`/`apiRequestFile`, and mirrors their exact
 * error-handling structure: the error envelope is parsed only in the
 * failure branch, and a success response's `{data}` is trusted directly.
 */
export async function apiRequestUpload<T>(path: string, file: File, fieldName: string): Promise<T> {
  const headers: Record<string, string> = {};
  const csrfToken = readCookie(CSRF_COOKIE_NAME);
  if (csrfToken) headers[CSRF_HEADER_NAME] = csrfToken;
  // Deliberately no Content-Type header — the browser sets the multipart
  // boundary itself when the fetch body is a FormData instance.

  const formData = new FormData();
  formData.append(fieldName, file);

  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: "POST",
    credentials: "include",
    headers,
    body: formData,
  });

  if (!response.ok) {
    const errorPayload = (await response.json().catch(() => null)) as { error?: ApiErrorPayload } | null;
    const error = errorPayload?.error ?? null;
    throw new ApiError(
      error?.code ?? "INTERNAL",
      error?.message ?? "Došlo je do neočekivane greške.",
      response.status,
      error?.details,
    );
  }

  const payload = (await response.json()) as { data: T };
  return payload.data;
}
